import { useFeatureFlags } from '@/lib/featureFlags'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import { useAllProperties } from '@/hooks/useProperties'

// D9 (EP-147): the module keys a route or a sidebar item can be gated on.
// Each predicate is copied verbatim from where it used to be computed
// (mostly Sidebar.tsx) so the sidebar and the router guard cannot drift:
// both now consume this one hook instead of re-deriving the same booleans.
export type ModuleKey =
  | 'condominium'
  | 'commonAreaFees'
  | 'visitorManagement'
  | 'debtCollection'
  | 'announcements'
  | 'accounting'
  | 'cmms'
  | 'incentivesRewards'
  | 'incentivesAny'
  | 'lpr'
  | 'parkingFee'

/**
 * Resolves whether a module is enabled for the current account.
 *
 * Returns `undefined` while the inputs a given module key depends on are
 * still loading, meaning useAccountSettings() for every key, and useAllProperties()
 * additionally for 'commonAreaFees'. useFeatureFlags() is a Suspense hook, so
 * flag values are always settled by the time this renders and never
 * contribute to the loading state.
 *
 * All three underlying hooks are called UNCONDITIONALLY on every render
 * (Rules of Hooks). The `module` argument only selects which computed
 * value is returned, never which hooks run.
 */
export function useModuleEnabled(module: ModuleKey): boolean | undefined {
  const featureFlags = useFeatureFlags()
  const { data: accountSettings, isLoading: accountSettingsLoading } = useAccountSettings()

  // EP-111 T111-8: CAF is dual-gated on condominium_enabled AND the account
  // having >=1 condominium-type property. useAllProperties() is only fetched
  // once condominiumEnabled is known, but it's still called unconditionally
  // here so the hook order never changes across renders.
  const condominiumEnabled = accountSettings?.settings?.condominium_enabled === true
  const { data: allProperties, isLoading: allPropertiesLoading } = useAllProperties(condominiumEnabled)

  if (accountSettingsLoading) return undefined

  switch (module) {
    case 'condominium':
      // Identical to Sidebar's showSharedOwners: condominium_enabled ONLY,
      // no >=1 condo property requirement (a co-owner can be added as soon
      // as the module is on).
      return condominiumEnabled
    case 'commonAreaFees': {
      if (!condominiumEnabled) return false
      if (allPropertiesLoading) return undefined
      const hasCondoProperty = (allProperties ?? []).some((p) => p.property_type === 'condominium')
      return hasCondoProperty
    }
    case 'visitorManagement':
      return featureFlags.visitorManagement && (accountSettings?.settings?.visitor_management_enabled ?? false)
    case 'debtCollection':
      return featureFlags.debtCollection && (accountSettings?.settings?.debt_collection_enabled ?? false)
    case 'announcements':
      return featureFlags.announcements && (accountSettings?.settings?.announcements_enabled ?? false)
    case 'accounting':
      // Setting-only, no flag, matching the accounting/microsite/lpr/
      // parking_fee/condominium single-gate exception.
      return accountSettings?.settings?.accounting_enabled ?? false
    case 'cmms':
      return featureFlags.cmms && (accountSettings?.settings?.cmms_enabled ?? false)
    case 'incentivesRewards':
      return featureFlags.loyaltyPoints && (accountSettings?.settings?.loyalty_points_enabled ?? false)
    case 'incentivesAny': {
      const incentivesRewards =
        featureFlags.loyaltyPoints && (accountSettings?.settings?.loyalty_points_enabled ?? false)
      const incentivesRetention =
        featureFlags.renewalBenefits && (accountSettings?.settings?.renewal_benefits_enabled ?? false)
      return incentivesRewards || incentivesRetention
    }
    case 'lpr':
      // Setting-only, no flag.
      return accountSettings?.settings?.lpr_enabled ?? false
    case 'parkingFee':
      // Setting-only, no flag.
      return accountSettings?.settings?.parking_fee_enabled ?? false
  }
}
